import React, { useMemo, useState } from 'react';
import { DialogBox } from '@mummssoftware/common-ui';
import { Pim } from '@mummssoftware/utils';
import ScheduledVisitApp from '../ScheduledVisitApp';
import defaults from '../translations/en.json';
import { PatientComplianceRow } from './data';

/**
 * OP-33263: clicking a row opens that patient's Visit Compliance calendar
 * inside the widget (the same way Visit Summary opens a provider's Visit
 * Log), starting on the month of the selected range. The calendar runs
 * standalone: it loads its own role groups and visits because no CPVF or
 * Visit Log widget feeds the global contexts on the clinical dashboard.
 */

type PatientComplianceModalProps = {
  patient: PatientComplianceRow;
  fromDate: string;
  toDate: string;
  agency?: string;
  keycloak: Keycloak.KeycloakInstance;
  currentUser?: mumms.User;
  pim: Pim;
  onClose: () => void;
};

const MODAL_WIDTH = '95vw';
const MODAL_HEIGHT = '92vh';

export const PatientComplianceModal = ({
  patient,
  fromDate,
  toDate,
  agency,
  keycloak,
  currentUser,
  pim,
  onClose,
}: PatientComplianceModalProps) => {
  const [open, setOpen] = useState(true);
  const closeModal = () => {
    setOpen(false);
    onClose();
  };

  const calendarPatient = useMemo(
    () =>
      (({
        patientNumber: patient.patientNumber,
        id: patient.patientId ?? undefined,
        firstName: patient.firstName,
        lastName: patient.lastName,
        siteId: patient.siteId ?? undefined,
      } as unknown) as mumms.patient),
    [patient],
  );

  return (
    <DialogBox
      open={open}
      handleClick={closeModal}
      disableBackdropClick
      maxWidth={false}
      hideBox
      style={{ overflow: 'hidden' }}
      data-testid="cpc-patient-modal"
    >
      <div
        style={{
          width: MODAL_WIDTH,
          height: MODAL_HEIGHT,
          maxHeight: 'calc(100vh - 32px)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <ScheduledVisitApp
          embedded
          online
          smallWidget={false}
          standalone
          initialDate={fromDate}
          rangeEnd={toDate}
          agency={agency}
          keycloak={keycloak}
          currentUser={currentUser}
          patient={calendarPatient}
          patientNumber={patient.patientNumber}
          pim={pim}
          appTitle={`${defaults.patientComplianceTitle} - ${patient.displayName}`}
          onClick={closeModal}
        />
      </div>
    </DialogBox>
  );
};

export default PatientComplianceModal;
